import React from "react";
import {
    Box,
    Typography,
    FormControlLabel,
    Switch,
    Divider,
    Button,
    Alert,
    Card,
    CardContent,
} from "@mui/material";
import { useColumnVisibility } from "../../hooks/useColumnVisibility";

interface ColumnConfig {
    id: string;
    label: string;
    description?: string;
}

const ORDER_COLUMNS: ColumnConfig[] = [
    {
        id: "order_number",
        label: "Order Number",
        description: "Show order number column",
    },
    {
        id: "customer_name",
        label: "Customer Name",
        description: "Show customer name column",
    },
    { id: "status", label: "Status", description: "Show order status column" },
    {
        id: "total_amount",
        label: "Total Amount",
        description: "Show total amount column",
    },
    {
        id: "total_cost_price",
        label: "Cost Price",
        description: "Show cost price column",
    },
    { id: "profit", label: "Profit", description: "Show profit column" },
    {
        id: "shipping_amount",
        label: "Delivery Charge",
        description: "Show delivery charge column",
    },
    {
        id: "delivery_service",
        label: "Delivery Service",
        description: "Show delivery service column",
    },
    {
        id: "order_channel",
        label: "Channel",
        description: "Show how the order reached us (POS, Website Form, etc.)",
    },
    {
        id: "order_date",
        label: "Order Date",
        description: "Show order date column",
    },
    {
        id: "payment_status",
        label: "Payment Status",
        description: "Show payment status column",
    },
    { id: "actions", label: "Actions", description: "Show actions column" },
];

export function ColumnVisibilitySettings() {
    const { columnVisibility, updateColumnVisibility, resetToDefaults } =
        useColumnVisibility();

    const handleToggle = (columnId: string) => {
        updateColumnVisibility(columnId, !columnVisibility[columnId]);
    };

    const handleReset = () => {
        resetToDefaults();
    };

    const visibleCount = Object.values(columnVisibility).filter(Boolean).length;

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Order Table Columns
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    Customize which columns are visible in the orders table.
                    Settings are saved locally in your browser.
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Currently showing {visibleCount} of {ORDER_COLUMNS.length}{" "}
                    columns
                </Alert>

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    {ORDER_COLUMNS.map((column) => (
                        <Box
                            sx={{
                                flex: {
                                    xs: "1 1 100%",
                                },
                                maxWidth: {
                                    sm: "calc(50% - 8px)",
                                    md: "calc(33.333% - 11px)",
                                },
                            }}
                            key={column.id}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            columnVisibility[column.id] || false
                                        }
                                        onChange={() => handleToggle(column.id)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                        >
                                            {column.label}
                                        </Typography>
                                        {column.description && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {column.description}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        </Box>
                    ))}
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Changes are automatically saved
                    </Typography>
                    <Button variant="outlined" onClick={handleReset}>
                        Reset to Defaults
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}
